import { useCallback, useEffect, useRef, useState } from "react";
import { api, friendlyError, logIssue, adminMediaUrl } from "@/lib/api";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Loader2, UploadCloud, Search, X, Check } from "lucide-react";

const FILTERS = [
  ["all", "Alle Typen"],
  ["image/jpeg", "JPEG"],
  ["image/png", "PNG"],
  ["image/webp", "WebP"],
  ["image/heic", "HEIC"],
];

export function useMediaLibrary(visibility = "public") {
  const [items, setItems] = useState(null);
  const [error, setError] = useState("");
  const [q, setQ] = useState("");
  const [ctype, setCtype] = useState("all");

  const load = useCallback(async () => {
    try {
      const { data } = await api.get("/admin/media", { params: { q, ctype, visibility } });
      setItems(data);
      setError("");
    } catch (e) {
      logIssue("GET /admin/media", e);
      setItems([]);
      setError(friendlyError(e));
    }
  }, [q, ctype, visibility]);

  useEffect(() => {
    load();
  }, [load]);

  return { items, error, load, q, setQ, ctype, setCtype };
}

export function MediaUploader({ onUploaded, visibility = "public" }) {
  const [progress, setProgress] = useState(null);
  const [error, setError] = useState("");
  const [dragging, setDragging] = useState(false);
  const inputRef = useRef(null);

  const upload = async (fileList) => {
    const files = Array.from(fileList || []);
    if (!files.length) return;
    setError("");
    for (const file of files) {
      if (file.size > 25 * 1024 * 1024) {
        setError(`${file.name}: größer als 25 MB.`);
        continue;
      }
      if (!/^image\/(jpeg|png|webp|heic|heif)$/i.test(file.type) && !/\.(jpe?g|png|webp|heic|heif)$/i.test(file.name)) {
        setError(`${file.name}: nur JPEG, PNG, WebP oder HEIC.`);
        continue;
      }
      const fd = new FormData();
      fd.append("file", file);
      fd.append("visibility", visibility);
      try {
        setProgress({ name: file.name, percent: 0 });
        const { data } = await api.post("/admin/media", fd, {
          timeout: 180000,
          onUploadProgress: (e) =>
            setProgress({ name: file.name, percent: e.total ? Math.round((e.loaded / e.total) * 100) : 50 }),
        });
        onUploaded?.(data);
      } catch (e) {
        logIssue("POST /admin/media", e);
        setError(friendlyError(e));
      } finally {
        setProgress(null);
      }
    }
    if (inputRef.current) inputRef.current.value = "";
  };

  return (
    <div>
      <div
        onDragOver={(e) => {
          e.preventDefault();
          setDragging(true);
        }}
        onDragLeave={() => setDragging(false)}
        onDrop={(e) => {
          e.preventDefault();
          setDragging(false);
          upload(e.dataTransfer.files);
        }}
        data-testid="media-dropzone"
        className={`flex flex-col items-center justify-center gap-3 border-2 border-dashed p-8 text-center transition-colors ${
          dragging ? "border-signal bg-signal/5" : "border-paper-line bg-paper"
        }`}
      >
        <UploadCloud size={26} className="text-charcoal" />
        <p className="ap-body text-charcoal">
          Bilder hierher ziehen oder auswählen – direkt von iPhone, iPad oder Computer.
        </p>
        <p className="ap-stamp">JPEG · PNG · WebP · HEIC · max. 25 MB</p>
        <input
          ref={inputRef}
          id="media-file-input"
          type="file"
          accept="image/jpeg,image/png,image/webp,image/heic,image/heif"
          multiple
          onChange={(e) => upload(e.target.files)}
          data-testid="media-file-input"
          className="sr-only"
        />
        <label htmlFor="media-file-input" className="ap-btn-solid cursor-pointer" data-testid="media-choose-button">
          Bilder auswählen
        </label>
      </div>

      {progress ? (
        <div className="mt-4" data-testid="media-progress">
          <p className="ap-stamp">
            {progress.name} · {progress.percent}%
          </p>
          <div className="mt-2 h-1 w-full bg-paper-line">
            <div className="h-1 bg-signal transition-all" style={{ width: `${progress.percent}%` }} />
          </div>
        </div>
      ) : null}

      {error ? (
        <p role="alert" data-testid="media-upload-error" className="mt-4 border border-destructive bg-destructive/5 p-3 text-sm text-destructive">
          {error}
        </p>
      ) : null}
    </div>
  );
}

export function MediaSearchBar({ q, setQ, ctype, setCtype }) {
  return (
    <div className="flex flex-wrap items-center gap-3">
      <div className="relative flex-1 min-w-[200px]">
        <Search size={15} className="absolute left-3 top-1/2 -translate-y-1/2 text-concrete" />
        <input
          value={q}
          onChange={(e) => setQ(e.target.value)}
          placeholder="Titel, Alt-Text oder Dateiname suchen"
          data-testid="media-search"
          className="ap-field pl-9"
        />
      </div>
      <select value={ctype} onChange={(e) => setCtype(e.target.value)} data-testid="media-filter-type" className="ap-field max-w-[180px]">
        {FILTERS.map(([v, l]) => (
          <option key={v} value={v}>
            {l}
          </option>
        ))}
      </select>
    </div>
  );
}

/** Modal picker used by every image field – no URL entry anywhere. */
export function MediaPicker({ open, onOpenChange, onSelect, currentId }) {
  const { items, error, load, q, setQ, ctype, setCtype } = useMediaLibrary("public");

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-h-[90vh] max-w-3xl overflow-y-auto rounded-none border-paper-line bg-paper">
        <DialogHeader>
          <DialogTitle className="font-display text-xl font-extrabold uppercase tracking-wide text-ink">
            Bild aus Medienbibliothek wählen
          </DialogTitle>
        </DialogHeader>

        <MediaUploader onUploaded={(m) => { load(); onSelect(m.id); }} />

        <div className="mt-5">
          <MediaSearchBar q={q} setQ={setQ} ctype={ctype} setCtype={setCtype} />
        </div>

        {error ? <p className="mt-4 text-sm text-destructive">{error}</p> : null}
        {items === null ? (
          <Loader2 className="mt-6 animate-spin text-charcoal" />
        ) : (
          <div className="mt-5 grid grid-cols-2 gap-3 sm:grid-cols-4" data-testid="media-picker-grid">
            {items.map((m, i) => (
              <button
                key={m.id}
                type="button"
                onClick={() => {
                  onSelect(m.id);
                  onOpenChange(false);
                }}
                data-testid={`media-pick-${i}`}
                className={`group relative border p-1 text-left transition-colors ${
                  currentId === m.id ? "border-signal" : "border-paper-line hover:border-ink"
                }`}
              >
                <img
                  src={adminMediaUrl(m.id, "thumb")}
                  alt={m.alt || m.title}
                  loading="lazy"
                  className="aspect-square w-full object-cover"
                />
                <span className="ap-stamp mt-2 block truncate px-1 pb-1">{m.title}</span>
                {currentId === m.id ? (
                  <Check size={16} className="absolute right-2 top-2 bg-signal p-0.5 text-white" />
                ) : null}
              </button>
            ))}
            {items.length === 0 ? (
              <p className="ap-body col-span-full text-charcoal" data-testid="media-picker-empty">
                Noch keine Bilder in der Bibliothek. Lade oben das erste Bild hoch.
              </p>
            ) : null}
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}

/** Image field replacement: thumbnail + picker + remove. */
export function MediaField({ label, value, onChange }) {
  const [open, setOpen] = useState(false);
  return (
    <div>
      <span className="ap-label">{label}</span>
      <div className="flex items-center gap-3">
        <div className="h-20 w-20 shrink-0 border border-paper-line bg-paper-subtle">
          {value ? (
            <img src={adminMediaUrl(value, "thumb")} alt="" className="h-full w-full object-cover" />
          ) : (
            <span className="ap-stamp flex h-full items-center justify-center text-[9px]">kein Bild</span>
          )}
        </div>
        <div className="flex flex-wrap gap-2">
          <button type="button" onClick={() => setOpen(true)} className="ap-btn-ghost" data-testid="media-field-choose">
            {value ? "Bild ändern" : "Bild wählen"}
          </button>
          {value ? (
            <button type="button" onClick={() => onChange("")} className="ap-btn-ghost" data-testid="media-field-clear">
              <X size={14} /> Entfernen
            </button>
          ) : null}
        </div>
      </div>
      <MediaPicker open={open} onOpenChange={setOpen} onSelect={onChange} currentId={value} />
    </div>
  );
}
